import type { Command } from '../../types/commands'
import { aboutCommand } from './about'
import { contactCommand } from './contact'
import { getSessionFileContent } from '../sessionFilesystem'
import { highlightSyntax } from '../syntaxHighlighter'
import { sampleFiles } from '../data/sampleFiles'
import { getContextDefaults } from '../../utils/contextUtils'
import postsData from '../../../blog/posts.json'

export const catCommand: Command = {
  name: 'cat',
  description: 'Display file contents',
  usage: 'cat [file]',
  category: 'navigation',
  acceptsFiles: true,
  acceptsDirectories: false,
  execute: async (args, context) => {
    if (args.length === 0) {
      return [
        {
          type: 'error',
          content: 'cat: missing file operand\nUsage: cat [file]',
        },
      ]
    }

    const file = args[0]
    const { currentDirectory, sessionFs } = getContextDefaults(context)

    // Check for blog posts in /thoughts/ directory
    if (file.startsWith('/thoughts/') || file.startsWith('thoughts/')) {
      const slug = file
        .replace(/^\/thoughts\//, '')
        .replace(/^thoughts\//, '')
        .replace(/\.md$/, '')

      const post = postsData.find(p => p.slug === slug)

      if (post) {
        try {
          const response = await fetch(`/blog/posts/${slug}.md`)
          if (response.ok) {
            const content = await response.text()
            const highlighted = highlightSyntax(content, slug + '.md')
            return [
              {
                type: 'info',
                content: (
                  <div dangerouslySetInnerHTML={{ __html: highlighted }} />
                ),
              },
            ]
          }
        } catch (error) {
          console.error('Error fetching blog post:', error)
        }
      }

      return [
        {
          type: 'error',
          content: `cat: ${file}: No such file or directory\n\nType 'blog' to see available posts`,
        },
      ]
    }

    // Check session filesystem first
    const fullPath = currentDirectory === '~' ? file : `${currentDirectory}/${file}`
    const sessionContent = getSessionFileContent(sessionFs, fullPath)

    if (sessionContent !== null) {
      const highlighted = highlightSyntax(sessionContent, file)
      return [
        {
          type: 'info',
          content: (
            <div dangerouslySetInnerHTML={{ __html: highlighted || '(empty file)' }} />
          ),
        },
      ]
    }

    // Fall back to static files
    const fileLower = file.toLowerCase()

    // Hidden .theme file
    if (fileLower === '.theme' || fileLower === '~/.theme') {
      return [
        {
          type: 'info',
          content: `
╔═══════════════════════════════════════════════════════════╗
║                     .theme                                 ║
╚═══════════════════════════════════════════════════════════╝

VISUAL THEME CONTROL

Usage: theme [mode]

Available modes:
  professional  - Clean, minimal terminal aesthetic
  horror        - Intense glitch effects and animations

Current: Check the header bar or try switching modes!

Example:
  $ theme horror
  $ theme professional

~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
 💡 You found a hidden feature! Try it out.
~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
`,
        },
      ]
    }

    // Hidden .secrets file
    if (fileLower === '.secrets' || fileLower === '~/.secrets') {
      return [
        {
          type: 'info',
          content: `
╔═══════════════════════════════════════════════════════════╗
║                     .secrets                               ║
╚═══════════════════════════════════════════════════════════╝

▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀
 Easter Eggs Found: Check these out!
▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄▄

Try these hidden commands:
  • konami    - A classic code
  • matrix    - Follow the white rabbit
  • hack      - Initiate hacking sequence
  • sudo      - Try elevated privileges
  • coffee    - Brew some energy
  • 42        - The answer to everything

More to discover... keep exploring!

~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
 Curiosity rewarded! 🎉
~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~·~
`,
        },
      ]
    }

    // About file
    if (fileLower === 'about.txt' || fileLower === '~/about.txt') {
      return aboutCommand.execute([])
    }

    // Contact file
    if (fileLower === 'contact.txt' || fileLower === '~/contact.txt') {
      return contactCommand.execute([])
    }

    // README
    if (fileLower === 'readme.md' || fileLower === '~/readme.md' || fileLower === 'readme') {
      return [
        {
          type: 'info',
          content: `
# Anderson Reinkordt - Portfolio Terminal

Welcome to my interactive portfolio!

## Available Commands:
- help     : Show all available commands
- about    : Learn about me
- projects : View my work
- skills   : See technical expertise
- experience : Work history
- contact  : Get in touch
- blog     : Read my thoughts

## Philosophy:
"To teach is to learn"

I'm dedicated to building accessible AI tools and
technology for underserved communities.

Type 'help' to get started!
`,
        },
      ]
    }

    // Check for sample files (imported from data/sampleFiles.ts)
    const samplePath = fullPath.replace(/^~\//, '')
    if (sampleFiles[samplePath]) {
      const content = sampleFiles[samplePath]
      const filename = file.split('/').pop() || file
      const highlighted = highlightSyntax(content, filename)
      return [
        {
          type: 'info',
          content: (
            <div dangerouslySetInnerHTML={{ __html: highlighted }} />
          ),
        },
      ]
    }

    return [
      {
        type: 'error',
        content: `cat: ${file}: No such file or directory\n\nTry 'ls' to see available files`,
      },
    ]
  },
}
